import { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { cloneDeep } from 'lodash'
import {
  AccountDetail,
  ConversationHistory,
  DataList,
  ImageSlider,
  LocationList,
  MultipleColumnsDataList,
  Note,
  ServiceDetail
} from '../../components/3modules/TabWidgets'
import { useNavigate, useParams } from 'react-router-dom'
import ApiService from '../../common/ApiCalls'
import { GlobalContext } from '../../services/context/DataContext'
import { formatDate } from '../../services/util/formatter'
import {
  useSelector,
  useDispatch
} from '../../common/redux.Interface.Component'
import {
  providerDataFetch,
  providerDataUpdate,
  consumerDataFetch,
  consumerDataUpdate
} from './reducers/actions'
import {
  deepCopyObject,
  deepCopyObjectArray,
  moveElementInArrayAt
} from '../../services/util/miscellaneousService'

const useDynamicTabs = admin => {
  const dispatch = useDispatch()
  const cardList = useSelector(state => state.tabs.cardList)
  const serviceList = useSelector(state => state.tabs.serviceList)
  const loading = useSelector(state => state.tabs.loading)
  const metaList = useSelector(state => state.tabs.metaList)
  const defaultData = useSelector(state => state.tabs.defaultData)

  const {
    selectedText,
    isLoadingContext,
    hasNoDataContext,
    selectedServiceData,
    contactContext,
    addressContext,
    serviceListContext,
    servicesCategorizedByServiceTypeContext,
    activeTabData,
    tabsData
  } = useContext(GlobalContext)

  const [selectedLoc] = selectedText
  const [hasNoData, setHasNoData] = hasNoDataContext
  const [selectedService, setSelectedService] = selectedServiceData
  const navigate = useNavigate()
  const { tabName, id } = useParams()
  const [isEditMode, setIsEditMode] = useState(false)
  const [layoutData, setLayoutData] = useState([])
  const [layoutDefaultData, setLayoutDefaultData] = useState([])
  const [isLoading, setIsLoading] = isLoadingContext

  const [snackbar, setSnackbar] = useState({
    isOpen: false,
    severity: 'success',
    message: 'Information is successfully updated'
  })
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [targetField, setTargetField] = useState({
    cardIndex: '',
    fieldIndex: ''
  })

  const [, setAddress] = addressContext
  const [, setContact] = contactContext
  const [, setServiceList] = serviceListContext
  const [, setServicesCategorizedByServiceType] =
    servicesCategorizedByServiceTypeContext
  const [, setActiveTab] = activeTabData
  const [tabs] = tabsData

  const isTab1 = tabName === 'tab1'
  const isTab2 = tabName === 'tab2'

  const isAllowedToViewCurrentTab = () => {
    for (let i = 0; i < tabs.length; i++) {
      if (tabs[i].sys_name === tabName && !tabs[i].isDisabled) {
        return true
      }
    }
    return false
  }

  const formatFrontendViewData = useMemo(() => (layoutData) => {
    const finalArray = []
    for (let i = 0; i < cardList.length; i++) {
      const { cardData, sysName: card_sys_name } = layoutData[i]

      for (let j = 0; j < cardData.length; j++) {
        if (layoutData[i].cardData[j] === undefined) continue

        const {
          fieldId: field_id,
          fieldData,
          sysName: field_sys_name
        } = layoutData[i].cardData[j]

        if (isTab1) {
          finalArray.push({
            field_id,
            location_id: id,
            data: fieldData || '',
            card_sys_name,
            field_sys_name,
            contact_id: selectedService.contact_id
          })
        } else if (isTab2) {
          finalArray.push({
            field_id,
            location_id: id,
            data: fieldData || '',
            card_sys_name,
            field_sys_name,
            service_id: selectedService.id
          })
        } else {
          finalArray.push({
            field_id,
            location_id: id,
            data: fieldData || '',
            card_sys_name,
            field_sys_name
          })
        }
      }
    }
    return finalArray
  }, [cardList, id, isTab1, isTab2, selectedService])

  const formatFrontendLayoutData = useCallback((layoutData) => {
    const finalArray = []
    for (let i = 0; i < layoutData.length; i++) {
      for (let j = 0; j < layoutData[i].cardData.length; j++) {
        if (layoutData[i].cardData[j] === undefined) continue

        const {
          cardId: card_id,
          isHidden: card_custom_is_hidden,
          label: card_custom_name,
          type: card_custom_type
        } = layoutData[i]

        const {
          fieldId: field_id,
          isArchived: field_custom_is_archived,
          isHidden: field_custom_is_hidden,
          isMandatory: field_custom_is_mandatory,
          label: field_custom_name,
          type: field_custom_type
        } = layoutData[i].cardData[j]
        const dataItem = {
          card_id,
          card_custom_name,
          card_custom_position: i,
          card_custom_is_hidden,
          card_custom_is_archived: false,
          card_custom_is_mandatory: false,
          card_custom_type,
          field_id,
          field_custom_name,
          field_custom_position: j,
          field_custom_is_hidden,
          field_custom_is_archived,
          field_custom_is_mandatory,
          field_custom_type
        }
        finalArray.push(dataItem)
      }
    }
    return finalArray
  }, [])

  const handleSave = useCallback(async (values) => {
    setIsLoading(true);
    const updatedValues = formatFrontendViewData(values.values);
  
    try {
      await dispatch(consumerDataUpdate({ id, tabName, updatedValues })).unwrap();
      dispatch(consumerDataFetch());
      setSnackbar({
        isOpen: true,
        severity: 'success',
        message: 'Information has been updated'
      });
      navigate(0);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, id, tabName, navigate, formatFrontendViewData, setIsLoading]);
  
  

  const handleSaveAdmin = useCallback(async () => {
    setIsLoading(true);
    const updatedValues = formatFrontendLayoutData(layoutData);
  
    try {
      await dispatch(providerDataUpdate({ tabName, updatedValues })).unwrap();
      dispatch(providerDataFetch());
      setSnackbar({
        isOpen: true,
        severity: 'success',
        message: 'Information has been updated'
      });
      navigate(0);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, tabName, layoutData, navigate, formatFrontendLayoutData, setIsLoading]);
  
  

  const handleCancel = useCallback(() => {
    setIsEditMode(false);
  }, []);
  

  const handleCancelAdmin = useCallback(() => {
    navigate('/adminDashboard');
  }, [navigate]);
  

  const widgetSwitcher = ({
    data,
    admin,
    index,
    formikProps,
    resetToDefault,
    reorderFields,
    toggleIsHidden,
    missingDataFieldVisibility,
    updateFieldLabel,
    updateDataType,
    updateCardLabel,
    isCardDirty,
    toggleDeleteConfirmationModal,
    updateCardLabelMultipleColumns,
    toggleIsHiddenCard
  }) => {
    const { type } = data
    const props = {
      admin,
      index,
      editMode: isEditMode,
      data,
      key: data.cardId,
      resetToDefault,
      reorderFields,
      toggleIsHidden,
      updateFieldLabel,
      updateDataType,
      updateCardLabel,
      isCardDirty: isCardDirty(index),
      toggleDeleteConfirmationModal,
      updateCardLabelMultipleColumns,
      toggleIsHiddenCard,
      missingDataFieldVisibility,
      layoutDefaultData
    }

    switch (type) {
      case 'Data List': // Data List
        return <DataList {...props} />
      case 'Note': // Note
        return <Note {...props} />
      case 'Image Slider': // Image Slider
        return <ImageSlider {...props} />
      // case 'Service List': // Service List
      // case 'Contact List': // Contact List
      //     return <ServiceList {...props} />
      case 'Service Detail': // Service Detail
      case 'Contact Detail': // Contact Detail
        return <ServiceDetail {...props} />
      case 'Account Detail': // Account Detail
        return <AccountDetail {...props} />
      case 'Location List': // Location List
        return <LocationList {...props} />
      case 'Conversation History': // Conversation History
        return <ConversationHistory {...props} />
      case 'Multiple Columns Data List': // Conversation History
        return <MultipleColumnsDataList {...props} />
      default:
        return ''
    }
  }

  const toggleDeleteConfirmationModal = useCallback((cardIndex, fieldIndex) => {
    setTargetField({ cardIndex, fieldIndex });
    setIsDeleteModalOpen(prevState => !prevState);
  }, []);
  

  const toggleUpdateConfirmationModal = useCallback(() => {
    setIsSaveModalOpen(prevState => !prevState);
  }, []);

  const createLegacyServiceList = useCallback(async (serviceIds) => {
    const serviceTypes = await ApiService().listServiceTypes();
    if (serviceIds.length > 0) {
      // Same as for partys, recreate the array from the old API as a temporary solution
      const serviceArray = [{ 'data': [{ 'service_details': [], 'field_details': [] }] }];

      for (let i = 0; i < serviceIds.length; i++) {
        const service = await ApiService().getServicesById(serviceIds[i]);
        let type = '';

        for (let j = 0; j < serviceTypes.length; j++) {
          if (service.type_id === serviceTypes[j].id) {
            type = serviceTypes[j].name;
            serviceArray[0].data[0].service_details.push({
              'id': service.id,
              "name": service.name,
              "description": service.description,
              "email": service.email,
              "phone": service.phone,
              "document_url_name": null,
              "type_id": service.type_id,
              "category_id": service.category_id,
              "created_at": service.created_at,
              "updated_at": service.updated_at,
              "suspended_at": service.suspended_at,
              "deleted_at": service.deleted_at,
              "bec_service_id": "",
              "last_modified": null,
              "service_type_id": service.type_id,
              "service_type": type,
              "deleted": 0
            });
          }
        }
      }
      return serviceArray;
    }
    return null;
  }, []);

  const layoutDataCopy = useMemo(() => cloneDeep(cardList), [cardList]);

  const getPeopleView = useCallback(async (selectedContactId) => {
    const data = await ApiService().getPartyById(selectedContactId)
    const {
      avatar_media_id,
      phone,
      email,
      id,
      created_at,
      first_name,
      last_name,
      records
    } = data
    const fullName = `${first_name} ${last_name}`

    //have to format the address with new data structure from api

    const addresses = `${data.addresses[0].unit || ''} ${data.addresses[0].street} ${data.addresses[0].city} ${data.addresses[0].region} ${data.addresses[0].country} ${data.addresses[0].postal}`

    const contactDetailCardIndex = cardList.findIndex(
      card => card.sysName === 'Card 13'
    )
    const personalComplianceCardIndex = cardList.findIndex(
      card => card.sysName === 'Card 14'
    )
    const workComplianceCardIndex = cardList.findIndex(
      card => card.sysName === 'Card 15'
    )
    const notesCardIndex = cardList.findIndex(
      card => card.sysName === 'Card 16'
    )
    if (
      contactDetailCardIndex < 0 ||
      personalComplianceCardIndex < 0 ||
      workComplianceCardIndex < 0 ||
      notesCardIndex < 0
    )
      return null

    const contactDetailCardDataArray =
      layoutDataCopy[contactDetailCardIndex].cardData
    const personalComplianceCardDataArray =
      layoutDataCopy[personalComplianceCardIndex].cardData
    const workComplianceCardDataArray =
      layoutDataCopy[workComplianceCardIndex].cardData
    const notesCardDataArray = layoutDataCopy[notesCardIndex].cardData

    for (let i = 0; i < contactDetailCardDataArray.length; i++) {
      const sysName = contactDetailCardDataArray[i].sysName
      switch (sysName) {
        case 'Field 77':
          contactDetailCardDataArray[i].fieldData = { fullName, avatar_media_id }
          break
        case 'Field 78':
          contactDetailCardDataArray[i].fieldData = phone
          break
        case 'Field 79':
          contactDetailCardDataArray[i].fieldData = email
          break
        case 'Field 80':
          contactDetailCardDataArray[i].fieldData = addresses
          break
        case 'Field 81':
          contactDetailCardDataArray[i].fieldData = id
          break
        case 'Field 82':
          contactDetailCardDataArray[i].fieldData = formatDate(created_at)
          break
        default:
          break
      }
    }
    if (records && records.length) {
      for (let i = 0; i < personalComplianceCardDataArray.length; i++) {
        const fieldId = personalComplianceCardDataArray[i].fieldId
        const targetedRecordIndex = records.findIndex(
          record => record.field_id === fieldId
        )
        if (targetedRecordIndex >= 0) {
          const { effective_date, document_url_name, expiry_date } =
            records[targetedRecordIndex]
          const effectiveDate = effective_date
            ? formatDate(effective_date, 'YYYY-MM-DD')
            : null
          const expiryDate = effective_date
            ? formatDate(expiry_date, 'YYYY-MM-DD')
            : null
          personalComplianceCardDataArray[i].fieldData = [
            effectiveDate,
            expiryDate,
            document_url_name?.url || ''
          ]
        }
      }

      for (let i = 0; i < workComplianceCardDataArray.length; i++) {
        const fieldId = workComplianceCardDataArray[i].fieldId
        const targetedRecordIndex = records.findIndex(
          record => record.field_id === fieldId
        )
        if (targetedRecordIndex >= 0) {
          const { effective_date, document_url_name, expiry_date } =
            records[targetedRecordIndex]
          const effectiveDate = effective_date
            ? formatDate(effective_date, 'YYYY-MM-DD')
            : null
          const expiryDate = effective_date
            ? formatDate(expiry_date, 'YYYY-MM-DD')
            : null
          workComplianceCardDataArray[i].fieldData = [
            effectiveDate,
            expiryDate,
            document_url_name?.url || ''
          ]
        }
      }

      for (let i = 0; i < notesCardDataArray.length; i++) {
        const fieldId = notesCardDataArray[i].fieldId
        const targetedRecordIndex = records.findIndex(
          record => record.field_id === fieldId
        )
        if (targetedRecordIndex >= 0) {
          const { effective_date, document_url_name, expiry_date } =
            records[targetedRecordIndex]
          const effectiveDate = effective_date
            ? formatDate(effective_date, 'YYYY-MM-DD')
            : null
          const expiryDate = effective_date
            ? formatDate(expiry_date, 'YYYY-MM-DD')
            : null
          notesCardDataArray[i].fieldData = [
            effectiveDate,
            expiryDate,
            document_url_name?.url || ''
          ]
        }
      }
    } else {
      for (let i = 0; i < personalComplianceCardDataArray.length; i++) {
        personalComplianceCardDataArray[i].fieldData = ['', '']
      }

      for (let i = 0; i < workComplianceCardDataArray.length; i++) {
        workComplianceCardDataArray[i].fieldData = ['', '']
      }

      for (let i = 0; i < notesCardDataArray.length; i++) {
        notesCardDataArray[i].fieldData = ['', '']
      }
    }

    setLayoutData(layoutDataCopy)
    setAddress(addresses)
    setContact(data)
  }, [cardList, setAddress, setContact, layoutDataCopy]);

  const getServiceView = useCallback(async (selectedServiceId) => {
    const raw = [selectedServiceId]
    const res = await createLegacyServiceList(raw)

    const service = res[0].data[0]

    const { name } = service.service_details[0] || ''

    const field_details = service.field_details.filter(function (record) {
      return record.location_id === id
    })

    const SERVICE_TITLE_IDX = 0

    const serviceDetailCardIndex = cardList.findIndex(
      card => card && card.sysName === 'Card 24'
    )
    const interiorDataCardIndex = cardList.findIndex(
      card => card && card.sysName === 'Card 25'
    )
    const specialNotesCardIndex = cardList.findIndex(
      card => card && card.sysName === 'Card 26'
    )
    const serviceDetailCardDataArray =
      layoutDataCopy[serviceDetailCardIndex]?.cardData ?? []
    const interiorDataCardDataArray =
      layoutDataCopy[interiorDataCardIndex]?.cardData ?? []
    const specialDataCardDataArray =
      layoutDataCopy[specialNotesCardIndex]?.cardData ?? []

    const makeArrayObj = fieldData => {
      if (fieldData === null || fieldData === '') return fieldData
      return [fieldData]
    }

    const parseFieldData = fieldData => {
      if (!fieldData || fieldData === 'null') return fieldData
      if (Array.isArray(fieldData)) return fieldData
      return JSON.parse(fieldData)
    }
    const setFieldValue = (idx, cardDataArray, fieldValueForEachService) => {
      const fieldObj = field_details.filter(
        it => it.field_id === cardDataArray[idx].fieldId
      )
      if (!fieldValueForEachService.length || !fieldObj.length) {
        cardDataArray[idx].fieldData = ''
      } else {
        cardDataArray[idx].fieldData = parseFieldData(
          makeArrayObj(fieldObj[0].data)
        ) // get first data from duplicated field Values
      }
    }

    const fieldValuesForSelectedService = field_details.filter(
      it => it.service_id === selectedServiceId
    )

    if (serviceDetailCardDataArray[SERVICE_TITLE_IDX])
      serviceDetailCardDataArray[SERVICE_TITLE_IDX].fieldData = name
    for (let i = 1; i < serviceDetailCardDataArray.length; i++) {
      setFieldValue(
        i,
        serviceDetailCardDataArray,
        fieldValuesForSelectedService
      )
    }

    for (let i = 0; i < interiorDataCardDataArray.length; i++) {
      setFieldValue(i, interiorDataCardDataArray, fieldValuesForSelectedService)
    }

    for (let i = 0; i < specialDataCardDataArray.length; i++) {
      setFieldValue(i, specialDataCardDataArray, fieldValuesForSelectedService)
    }

    setLayoutData(layoutDataCopy)
  }, [cardList, createLegacyServiceList, id, setLayoutData, layoutDataCopy]);

  const getServicesByid = useCallback(async () => {
    if (serviceList?.length) {
      const categorizedServices = categorizeServicesByServiceType(serviceList)
      const categories = Object.keys(categorizedServices)
      if (categories?.length) {
        setServiceList(categories)
        setSelectedService(categorizedServices[categories[0]][0])
        setActiveTab(categorizedServices[categories[0]][0].id)
      }

      setServicesCategorizedByServiceType(categorizedServices)
    } else {
      setHasNoData(true)
    }
  }, [serviceList, setActiveTab, setHasNoData, setSelectedService, setServiceList, setServicesCategorizedByServiceType]) 

  const getContactsByid = useCallback(async (formattedLayoutData) => {
    var partyArray = [{'data' : []}]
    const [res, partyTypes] = await Promise.all([
      ApiService().getAsset(id),
      ApiService().listContactTypes()
    ])
    //Until we have more time to rewrite this I'm recreating the array that the old API delieverd
    for (var i = 0; i < partyTypes.length; i++)
    {
      partyArray[0].data.push({
        'contact_type': partyTypes[i].name,
        'type_id': partyTypes[i].id,
        'contacts': []
      })
    }
    for (var p = 0; p < res.party_ids.length; p++)
    {
      let party = await ApiService().getPartyById(res.party_ids[p])
      if (party.type_id)
      {
        for (var j = 0; j < partyArray[0].data.length; j++)
        {
          if (partyArray[0].data[j].type_id === party.type_id)
          {
            partyArray[0].data[j].contacts.push({
              "contact_id": party.id,
              "location_id": id,
              "title": null,
              "first_name": party.first_name,
              "last_name": party.last_name,
              "job_title": party.job_title_id,
              "phone_number": party.phone,
              "email_address": party.email,
              "id_number": null,
              "assined_date": party.created_at,
              "contact_type": partyArray[0].data[j].contact_type,
              "avatar": party.avatar_media_id,
              "avatar_url": party.avatar_url,
              "isActive": false
            })
          }
        }
      }
    }

    const contactListCategorizedByContactType = partyArray[0].data
    const contactObjectListCategorizedByContactType =
      categorizeContactsByContactType(contactListCategorizedByContactType)
    const contactTypeList = Object.keys(
      contactObjectListCategorizedByContactType
    )

    if (contactTypeList.length) {
      setServiceList(contactTypeList)
      const firstContactOnList = findFirstContactOnList(
        contactTypeList,
        contactObjectListCategorizedByContactType,
        formattedLayoutData
      )

      if (firstContactOnList) {
        setSelectedService(firstContactOnList)
        setActiveTab(firstContactOnList.id)
      }

      setServicesCategorizedByServiceType(
        contactObjectListCategorizedByContactType
      )
    } else {
      setHasNoData(true)
    }
  }, [id, setActiveTab, setHasNoData, setSelectedService, setServiceList, setServicesCategorizedByServiceType]);

  const getTabViewData = useCallback(async () => {
    try {
      setLayoutData(cloneDeep(cardList)); // Use memoized deep copy
      if (tabName === 'tab1') {
        getContactsByid(cardList);
      }
      if (tabName === 'tab2') {
        getServicesByid(cardList);
      }
    } catch (e) {
      console.log(e);
    }
  }, [cardList, tabName, getContactsByid, getServicesByid]);

  const findFirstContactOnList = (
    contactTypeList,
    contactObjectListCategorizedByContactType,
    formattedLayoutData
  ) => {
    let index = 4
    for (let i = 0; i < contactTypeList.length; i++) {
      const indexOfContactType = formattedLayoutData.findIndex(
        card => card.label === contactTypeList[i]
      )
      if (index > indexOfContactType && indexOfContactType > -1)
        index = indexOfContactType
    }
    return (
      contactObjectListCategorizedByContactType[
        formattedLayoutData[index]?.label
      ]?.[0] ?? null
    )
  }

  const categorizeServicesByServiceType = categoriesArray => {
    const categorizedServicesObjectByServiceType = {}
    for (let i = 0; i < categoriesArray.length; i++) {
      if (
        categorizedServicesObjectByServiceType[categoriesArray[i].service_type]
      ) {
        categorizedServicesObjectByServiceType[
          categoriesArray[i].service_type
        ].push(categoriesArray[i])
      } else {
        categorizedServicesObjectByServiceType[
          categoriesArray[i].service_type
        ] = [categoriesArray[i]]
      }
    }
    return categorizedServicesObjectByServiceType
  }

  const categorizeContactsByContactType =
    contactListCategorizedByContactType => {
      const categorizedContactsObjectByContactType = {}
      for (let i = 0; i < contactListCategorizedByContactType.length; i++) {
        categorizedContactsObjectByContactType[
          contactListCategorizedByContactType[i].contact_type
        ] = contactListCategorizedByContactType[i].contacts
      }
      return categorizedContactsObjectByContactType
    }

  useEffect(() => {
    if (loading) return
    setHasNoData(false)
    handleCancel()
    if (admin) {
      getTabLayoutData()
    } else {
      getTabViewData()
    }
    setIsLoading(false)
    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [tabName, id, cardList, metaList, defaultData, loading])

  useEffect(() => {
    if (loading) return
    if (isTab1 && isAllowedToViewCurrentTab()) {
      const selectedContactId = selectedService.contact_id
      const condition = selectedContactId && cardList.length > 0
      if (condition) {
        getPeopleView(selectedContactId)
      }
    }

    if (isTab2 && isAllowedToViewCurrentTab()) {
      console.log(selectedService)
      const selectedServiceId = selectedService.id
      const condition = selectedServiceId && cardList.length > 0 && !loading

      if (condition) {
        getServiceView(selectedServiceId, id)
      }
    }
    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [selectedService, tabName, loading])

  const getTabLayoutData = async () => {
    if (loading) return
    setLayoutData(JSON.parse(JSON.stringify(metaList)))
    setLayoutDefaultData(JSON.parse(JSON.stringify(defaultData)))
  }

  const reorderCards = useCallback((newValue) => {
    setLayoutData(prevLayoutData => {
      for (let i = 0; i < newValue.length; i++) {
        newValue[i].position = i;
      }
      return newValue;
    });
  }, []);
  
    
  const resetAllToDefault = () => {
    reorderCards(deepCopyObjectArray(layoutDefaultData))
  }

  const resetToDefault = useCallback((index, targetCardId) => {
    setLayoutData(prevLayoutData => {
      const newLayoutData = [...prevLayoutData];
      const targetIndex = layoutDefaultData.findIndex(
        defaultData => defaultData.cardId === targetCardId
      );
      newLayoutData[index] = deepCopyObject(layoutDefaultData[targetIndex]);
      moveElementInArrayAt(newLayoutData, index, newLayoutData[index].position);
      return newLayoutData;
    });
  }, [layoutDefaultData]);

  const reorderFields = (newValue, index) => {
    setLayoutData(prevLayoutData => {
      const newLayoutData = [...prevLayoutData];
      newLayoutData[index].cardData = newValue;
      return newLayoutData;
    });
  };

  const toggleIsHidden = useCallback((cardIndex, fieldIndex) => {
    setLayoutData(prevLayoutData => {
      const newLayoutData = [...prevLayoutData];
      newLayoutData[cardIndex].cardData[fieldIndex].isHidden =
        !newLayoutData[cardIndex].cardData[fieldIndex].isHidden;
      return newLayoutData;
    });
  }, []);  

  const toggleIsHiddenCard = useCallback((cardIndex) => {
    setLayoutData(prevLayoutData => {
      const newLayoutData = [...prevLayoutData];
      newLayoutData[cardIndex].isHidden = !newLayoutData[cardIndex].isHidden;
      if (newLayoutData[cardIndex].isHidden) {
        newLayoutData.push(newLayoutData.splice(cardIndex, 1)[0]);
      }
      return newLayoutData;
    });
  }, []);  

  const updateFieldLabel = useCallback((cardIndex, fieldIndex, newValue) => {
    setLayoutData(prevState => {
      const prevStateCopy = [...prevState];
      prevStateCopy[cardIndex].cardData[fieldIndex].label = newValue;
      return prevStateCopy;
    });
  }, [setLayoutData]);
  

  const updateDataType = useCallback((cardIndex, fieldIndex, newDataType) => {
    setLayoutData(prevState => {
      const prevStateCopy = [...prevState];
      prevStateCopy[cardIndex].cardData[fieldIndex].type = newDataType;
      return prevStateCopy;
    });
  }, [setLayoutData]);
  

  const updateCardLabel = useCallback((cardIndex, newValue) => {
    setLayoutData(prevState => {
      const prevStateCopy = [...prevState];
      prevStateCopy[cardIndex].label = newValue;
      return prevStateCopy;
    });
  }, [setLayoutData]);
  

  const updateCardLabelMultipleColumns = (cardIndex, index, newValue) => {
    setLayoutData(prevState => {
      const prevStateCopy = [...prevState]
      const labelArray = JSON.parse(prevStateCopy[cardIndex].label)
      labelArray[index] = newValue
      prevStateCopy[cardIndex].label = JSON.stringify(labelArray)
      return prevStateCopy
    })
  }
  const isCardDirty = index => {
    const cardDataString = JSON.stringify(layoutData[index])
    const defaultCardDataIndex = layoutDefaultData.findIndex(
      card => card.cardId === layoutData[index].cardId
    )
    const defaultCardDataString = JSON.stringify(
      layoutDefaultData[defaultCardDataIndex]
    )

    return cardDataString !== defaultCardDataString
  }

  const deleteField = () => {
    const { cardIndex, fieldIndex } = targetField;
    setLayoutData(prevLayoutData => {
      const newLayoutData = [...prevLayoutData];
      newLayoutData[cardIndex].cardData[fieldIndex].isArchived =
        !newLayoutData[cardIndex].cardData[fieldIndex].isArchived;
      return newLayoutData;
    });
  };  

  return {
    isEditMode,
    setIsEditMode,
    layoutData,
    reorderCards,
    isLoading,
    setIsLoading,
    snackbar,
    setSnackbar,
    handleSave,
    handleCancel,
    resetAllToDefault,
    handleSaveAdmin,
    handleCancelAdmin,
    widgetSwitcher,
    selectedLoc,
    resetToDefault,
    reorderFields,
    toggleIsHidden,
    updateFieldLabel,
    updateDataType,
    updateCardLabel,
    isCardDirty,
    toggleDeleteConfirmationModal,
    deleteField,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    updateCardLabelMultipleColumns,
    toggleIsHiddenCard,
    isSaveModalOpen,
    setIsSaveModalOpen,
    toggleUpdateConfirmationModal,
    hasNoData,
    missingDataFieldVisibility: false,
    layoutDefaultData
  }
}

export default useDynamicTabs
